"use client";

import { useEffect, useReducer, useRef } from "react";
import { ALL_MODULES } from "@/lib/modules";
import { monotonicState } from "@/lib/sse/state-primitives";
import { uiReducer }         from "@/lib/sse/reducers/ui";
import { documentsReducer }  from "@/lib/sse/reducers/documents";
import { financeReducer }    from "@/lib/sse/reducers/finance";
import { complianceReducer } from "@/lib/sse/reducers/compliance";
import { logisticsReducer }  from "@/lib/sse/reducers/logistics";

// CDM canonical envelope, mirrored from shared/schema/envelope.schema.json.
export type CdmEvent = {
  eventId: string;
  eventType: string;
  eventVersion: string;
  occurredAt: string;
  recordedAt: string;
  tenantId: string;
  corridorId: string;
  aggregateRoot: { type: string; id: string };
  causationId?: string;
  correlationId?: string;
  sagaInstanceId?: string;
  actor: { type: string; id: string; displayName?: string };
  payload: Record<string, unknown>;
  evidenceAnchorRef?: {
    hcsTopicId?: string;
    hcsSequence?: number;
    contentHash?: string;
    anchoredAt?: string;
  };
};

export type AgentLine = {
  id: string;
  agent: string;
  line: string;
  lineKey?: string;
  occurredAt: number;
};

export type ScreenSelection = {
  screen: string;          // e.g. "0.1", "1.4"
  perspective: string;     // "E", "I-AM", "I-SV", "E-GP", "split"
  label: string;
  changedAt: number;
};

export type ContractAggregate = {
  id: string;
  incoterms?: string;
  hsCode?: string;
  commodity?: string;
  quantityKg?: number;
  priceUsd?: number;
  deliveryWindowDays?: number;
};

export type DocumentLifecycleState =
  | "Created"
  | "Hashed"
  | "Attested"
  | "Transmitted"
  | "Validated";

export type DocumentRecord = {
  type: string;
  state: DocumentLifecycleState;
  issuer?: string;
  attestorOrbid?: string;
  attestorLei?: string;
};

export type LogisticsMilestone = {
  milestone: "Loading" | "Departure" | "Arrival" | string;
  vesselImo?: string;
  tempC?: number;
  occurredAt: number;
};

export type ComplianceAuthorityRow = {
  name: string;
  outcome: "PASS" | "FAIL" | "INDETERMINATE";
  note?: string;
  snapshotAt?: string;
};

export type ComplianceSnapshot = {
  snapshotId: string;
  corridorId: string;
  authorities: ComplianceAuthorityRow[];
  vatPrefillSavingPct?: number;
  producedAt: number;
};

export type ReconciliationCellState = "pending" | "matched" | "auto-resolved" | "failed";

export type ReconciliationState = {
  status: "idle" | "started" | "completed";
  /** Map of "rowDoc::field" → cell state. Only fields the scenario surfaces
   *  are tracked; the renderer fills the rest with "matched" optimistically
   *  once status flips to "completed". */
  cells: Record<string, ReconciliationCellState>;
  weightToleranceAutoResolved: boolean;
  reportId?: string;
};

export type LcComposerState = {
  open: boolean;
  tenorOptionsDays: number[];
  tenorIndependent: boolean;
};

export type FundingMix = {
  buyerWorkingCapitalPct: number;
  bankFinancingPct: number;
  bankPartnerOrbid: string | null;
  bankBadge?: "FAB" | "Itau" | null;
};

export type LcIssuance = {
  lcReference: string;
  amountUsd: number;
  expiry: string;
  buyerSideBankBadge: "FAB" | "Itau" | null;
  fundingMix: FundingMix | null;
  evidenceAnchor?: { hcsTopicId?: string };
  issuedAt: number;
};

export type SettlementLeg = { leg: string; rail: string; occurredAt: number };

export type EventLogState = {
  events: CdmEvent[];
  // Derived projections — kept here so every consuming component reads from
  // the same shape rather than computing it independently.
  modulesUnlocked: Set<string>;
  pillarFill: { verify: number; trace: number; finance: number };
  invoiceState: Record<string, string>;
  policyDecisions: CdmEvent[];
  reputation: Record<string, Record<string, unknown>>;
  splitScreenHandshakes: number[];
  currentScreen: ScreenSelection | null;
  currentAct: { act: number; label: string } | null;
  agentLines: AgentLine[];
  contract: ContractAggregate | null;
  documents: Record<string, DocumentRecord>;
  reconciliation: ReconciliationState;
  logisticsMilestones: LogisticsMilestone[];
  customsCleared: { dubaiTrade: boolean; moccae: boolean };
  complianceSnapshot: ComplianceSnapshot | null;
  lcComposer: LcComposerState;
  fundingMix: FundingMix | null;
  lcIssuance: LcIssuance | null;
  settlementLegs: SettlementLeg[];
  threeFieldMatched: { var: boolean; e2eId: boolean; invoiceNumber: boolean } | null;
  reputationBefore: Record<string, Record<string, unknown>>;
};

export const initialState: EventLogState = {
  events: [],
  modulesUnlocked: new Set(),
  pillarFill: { verify: 0, trace: 0, finance: 0 },
  invoiceState: {},
  policyDecisions: [],
  reputation: {},
  splitScreenHandshakes: [],
  currentScreen: null,
  currentAct: null,
  agentLines: [],
  contract: null,
  documents: {},
  reconciliation: { status: "idle", cells: {}, weightToleranceAutoResolved: false },
  logisticsMilestones: [],
  customsCleared: { dubaiTrade: false, moccae: false },
  complianceSnapshot: null,
  lcComposer: { open: false, tenorOptionsDays: [30, 60, 120], tenorIndependent: true },
  fundingMix: null,
  lcIssuance: null,
  settlementLegs: [],
  threeFieldMatched: null,
  reputationBefore: {},
};

// M2 — top-level reducer becomes a thin dispatcher that pipes through
// per-domain reducer slices. Each slice handles only its own events and
// returns the same reference when not relevant (no unnecessary
// allocations). The events array (audit trail) is appended once at this
// level so individual slices don't each need to know about it.
//
// The order of slice invocation does NOT matter for correctness —
// every event type is handled by at most ONE state-mutation path per
// slice, and slices that touch different state fields can't conflict
// on the same field. The one exception is `policy.Decided`, which the
// compliance slice appends to the policyDecisions log AND the
// documents slice reads to update the reconciliation matrix — both
// fire independently and modify disjoint slices.
export function reduce(state: EventLogState, ev: CdmEvent): EventLogState {
  // Append the event to the audit trail. Per-domain slices receive a
  // state that already includes the event so anything that pivots on
  // ev presence (e.g. agent bubbles' eventId) reads consistently.
  let s: EventLogState = { ...state, events: [...state.events, ev] };
  s = uiReducer(s, ev);
  s = documentsReducer(s, ev);
  s = financeReducer(s, ev);
  s = complianceReducer(s, ev);
  s = logisticsReducer(s, ev);
  return s;
}


// DOC_STATE_ORDER + monoDocState + mapDocs moved to lib/sse/reducers/helpers.ts (M2).

/**
 * Subscribe to one of the SSE endpoints exposed by the Go backend.
 *   useSSE("exporter") — Aurora / Gulf Phosphate views
 *   useSSE("importer") — Al Madar / Pampagro views
 *
 * The hook accumulates events into a derived state matching the shape every
 * component reads. It is the single read-model for the entire UI; perspective
 * swap is a view-layer concern only (CDM events are corridor-scoped, not
 * perspective-scoped).
 */
export function useSSE(perspective: "exporter" | "importer", baseUrl?: string): EventLogState {
  const [state, dispatch] = useReducer(reduce, initialState);
  const ref = useRef<EventSource | null>(null);

  useEffect(() => {
    // Default to the same-origin Next App Router route under /api so the
    // demo works through a single tunneled URL. Pass an explicit baseUrl
    // to bypass (e.g., the headless recording script).
    const url = `${baseUrl ?? "/api"}/sse/${perspective}`;
    const es = new EventSource(url);
    ref.current = es;

    // The backend dispatches every CDM envelope on the default `message`
    // channel; type discrimination happens inside the reducer via
    // ev.eventType. One listener captures every event.
    es.onmessage = (e) => {
      try {
        dispatch(JSON.parse(e.data));
      } catch {
        // ignore malformed frames
      }
    };

    return () => {
      es.close();
      ref.current = null;
    };
  }, [perspective, baseUrl]);

  return state;
}
